import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import {
  DEFAULT_LANGUAGE,
  normalizeLanguage,
  translate,
  type Language,
  type Translate,
  type TranslationKey,
  type TranslationParams,
} from './catalogue';

/** Versioned, so a future change of shape cannot be read as this one. */
const STORAGE_KEY = 'iamok.language.v1';

interface LanguageContextValue {
  language: Language;
  /** The picker. Writes the stored override, which outranks everything below it. */
  setLanguage: (language: Language) => void;
  /**
   * The language a check is in, from `StatusResponse`. A receiver who never
   * touches the picker reads the monitor page in the creator's language — the
   * same one the email that sent them there was written in. Absent until the
   * backend chunk deploys, and absent is not an error.
   */
  setCheckLanguage: (language: string | null | undefined) => void;
  t: Translate;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

function readStoredLanguage(): Language | undefined {
  try {
    return normalizeLanguage(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    // Safari in private mode throws rather than returning null.
    return undefined;
  }
}

function storeLanguage(language: Language): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, language);
  } catch {
    // A browser that refuses to remember is not a reason to refuse to render.
  }
}

/**
 * Resolves the active language, in this order:
 *
 *   ?lang= → stored override → the check's language → navigator.language → en
 *
 * Every rung is skipped when it has nothing to say. An unrecognised value —
 * `?lang=fr`, a `navigator.language` we do not speak — falls through as if it
 * were absent, never down to English by force.
 */
export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { search } = useLocation();

  // Read during render rather than in an effect, so a link shared as
  // `?lang=de` never paints a frame of English before correcting itself.
  const requested = normalizeLanguage(new URLSearchParams(search).get('lang'));

  const [chosen, setChosen] = useState<Language | undefined>(
    () => requested ?? readStoredLanguage(),
  );
  const [fromCheck, setFromCheck] = useState<Language | undefined>(undefined);

  // `?lang=` does not survive navigation, so it has to write through: without
  // this the language would evaporate on the visitor's first click, which is
  // the one thing that would make a shared link useless.
  useEffect(() => {
    if (!requested) return;
    storeLanguage(requested);
    setChosen(requested);
  }, [requested]);

  const language =
    chosen ?? fromCheck ?? normalizeLanguage(window.navigator.language) ?? DEFAULT_LANGUAGE;

  // index.html ships a static lang="en" as the pre-hydration default; from here
  // on the attribute follows what is actually on the page, so a screen reader
  // pronounces it correctly and the browser offers the right translation prompt.
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = useCallback((next: Language) => {
    storeLanguage(next);
    setChosen(next);
  }, []);

  // Assigns rather than merges: a check with no language has to clear whatever
  // the last one said, or the chain would quietly keep a rung it no longer has.
  const setCheckLanguage = useCallback((value: string | null | undefined) => {
    setFromCheck(normalizeLanguage(value));
  }, []);

  const t = useCallback<Translate>(
    (key: TranslationKey, params?: TranslationParams) => translate(language, key, params),
    [language],
  );

  const value = useMemo(
    () => ({ language, setLanguage, setCheckLanguage, t }),
    [language, setLanguage, setCheckLanguage, t],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export function useLanguage(): LanguageContextValue {
  const value = useContext(LanguageContext);
  if (!value) {
    throw new Error('useLanguage must be called inside a LanguageProvider');
  }
  return value;
}
